import { useState } from 'react';
import { X, Tag, Globe, Zap, Calendar, AlignLeft } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { NICHE_IMAGES } from '../../utils/constants';

export default function AddDealDrawer() {
  const {
    selectedGroup,
    showAddDealDrawer,
    setShowAddDealDrawer,
    addDeal,
  } = useAppState();

  const [form, setForm] = useState({
    brand: '',
    website: '',
    offer: '',
    expiry: '',
    desc: '',
  });

  if (!showAddDealDrawer || !selectedGroup) return null;

  const handleSubmit = () => {
    if (!form.brand || !form.offer) return;

    // Get image based on group niche
    const nicheImages = NICHE_IMAGES[selectedGroup.niche] || NICHE_IMAGES.general;
    const image = nicheImages[0];

    addDeal(selectedGroup.id, {
      brand: form.brand,
      logo: form.brand.charAt(0).toUpperCase(),
      color: 'bg-indigo-600',
      offer: form.offer,
      desc: form.desc || `Shared deal from ${form.brand}`,
      terms: form.desc ? `Shared member deal. ${form.desc}` : `Shared member deal for ${form.brand}.`,
      website: form.website,
      expiry: form.expiry,
      price: 'Varies',
      groupSize: '1/5 Joined',
      image,
    });

    // Reset form and close
    setForm({ brand: '', website: '', offer: '', expiry: '', desc: '' });
    setShowAddDealDrawer(false);
  };

  const handleClose = () => {
    setForm({ brand: '', website: '', offer: '', expiry: '', desc: '' });
    setShowAddDealDrawer(false);
  };

  const isValid = form.brand && form.offer;

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={handleClose} />

      {/* Panel */}
      <div className="slide-panel">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-bold">Post a New Deal</h2>
              <p className="text-xs text-gray-500 font-medium">Shared with {selectedGroup.name}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Brand Name */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
              Brand Name *
            </p>
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-2xl border border-gray-100 focus-within:border-indigo-200 transition-all">
              <Tag size={18} className="text-indigo-400" />
              <input
                type="text"
                placeholder="e.g. Nike"
                className="bg-transparent flex-1 text-sm font-bold outline-none text-gray-900 placeholder-gray-400"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
            </div>
          </div>

          {/* Website Link */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
              Website Link
            </p>
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-2xl border border-gray-100 focus-within:border-indigo-200 transition-all">
              <Globe size={18} className="text-indigo-400" />
              <input
                type="text"
                placeholder="https://..."
                className="bg-transparent flex-1 text-sm font-bold outline-none text-gray-900 placeholder-gray-400"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
          </div>

          {/* Offer / Deal */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
              Offer / Deal *
            </p>
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-2xl border border-gray-100 focus-within:border-indigo-200 transition-all">
              <Zap size={18} className="text-amber-400" />
              <input
                type="text"
                placeholder="e.g. 5% Back"
                className="bg-transparent flex-1 text-sm font-bold outline-none text-gray-900 placeholder-gray-400"
                value={form.offer}
                onChange={(e) => setForm({ ...form, offer: e.target.value })}
              />
            </div>
          </div>

          {/* Expiry Date */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
              Expiry Date
            </p>
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-2xl border border-gray-100 focus-within:border-indigo-200 transition-all">
              <Calendar size={18} className="text-indigo-400" />
              <input
                type="date"
                className="bg-transparent flex-1 text-sm font-bold outline-none text-gray-600"
                value={form.expiry}
                onChange={(e) => setForm({ ...form, expiry: e.target.value })}
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
              Description
            </p>
            <div className="flex items-start gap-3 bg-gray-50 px-4 py-3 rounded-2xl border border-gray-100 focus-within:border-indigo-200 transition-all">
              <AlignLeft size={18} className="text-indigo-400 mt-1" />
              <textarea
                rows={3}
                placeholder="Why join this deal?"
                className="bg-transparent flex-1 text-sm font-bold outline-none resize-none text-gray-900 placeholder-gray-400"
                value={form.desc}
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
              />
            </div>
          </div>
          </div>

          {/* Submit Button */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={handleSubmit}
              disabled={!isValid}
              className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-95 transition-all uppercase tracking-widest text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to Group
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
